import React from 'react';

import { iTag } from '../../../../core/models/Tag';
import Select from '../../../../components/Select/Select';
import Button from '../../../../components/Button/Button';
import { NewsTableFilters } from '../../NewsTable/useNewsTableFilters';
import { SortKey } from '../../NewsTable/useGetTableColumns';
import { IconArrowDown, IconClose, IconReset, IconSearch } from '../../../../icons';
import MobileSortDropdown from './MobileSortDropdown';

interface FilterBarProps {
    tagsOptions: iTag[];
    selectedTags: iTag[];
    filters: NewsTableFilters;
    onChange: (next: FilterBarProps['filters']) => void;
    localSearchQuery: string;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSearchClear: () => void;
    hasFilters: boolean;
    getRem: (px: number) => number;
    isTagsDropdownDisabled?: boolean;
    searchInputRef?: React.RefObject<HTMLInputElement>;
    onSearchBlur?: () => void;
    isMobile: boolean;
    onSortChange: (orderBy: SortKey, sortOrder: 'asc' | 'desc') => void;
}

const FiltersBar: React.FC<FilterBarProps> = ({
    tagsOptions,
    selectedTags,
    filters,
    onChange,
    localSearchQuery,
    onSearchChange,
    onSearchClear,
    hasFilters,
    getRem,
    isTagsDropdownDisabled,
    searchInputRef,
    onSearchBlur,
    isMobile,
    onSortChange
}) => {
    const shouldShowResetButton = (hasFilters: boolean, searchText: string): boolean => {
        return hasFilters || !!searchText.trim();
    };

    return (
        <div className="NewsBrowseSection__filters-container">
            <div className="NewsBrowseSection__dropdown">
                <span className="NewsBrowseSection__label">Browse by:</span>
                <div className="NewsBrowseSection__selects-row">
                    <Select
                        inputButton
                        isDisabled={isTagsDropdownDisabled}
                        buttonContent={(isOpen: boolean) => (
                            <span className="NewsBrowseSection__button-label">
                                Tags
                                <IconArrowDown
                                    style={{
                                        transform: isOpen ? 'rotate(180deg)' : undefined,
                                        transition: 'transform 0.2s ease',
                                        fontSize: '12px'
                                    }}
                                />
                            </span>
                        )}
                        options={tagsOptions}
                        value={selectedTags.map((t) => t.id)}
                        onChange={(ids) => {
                            const newTagIds: number[] = [];
                            ids.forEach((id) => {
                                newTagIds.push(id as number);
                            });
                            onChange({
                                ...filters,
                                tagsIds: newTagIds
                            });
                        }}
                        isSearchable
                        searchPlaceholder="Search"
                        autoFocus={false}
                        dropdownHeight={350}
                        styles={{ selectDropdown: { minWidth: getRem(270) } }}
                        isMultiple
                        isCheckboxHidden={false}
                        closeOnSelect={false}
                        hasApplyCancel
                        preventResetOnSelected
                        useBrowseStyles
                    />

                    {isMobile && (
                        <MobileSortDropdown orderBy={filters.orderBy} sortOrder={filters.sortOrder} onChange={onSortChange} />
                    )}
                </div>

                <div className="NewsBrowseSection__search">
                    <div className="NewsBrowseSection__search-field">
                        <div className="NewsBrowseSection__search-icon">
                            <IconSearch />
                        </div>
                        <input
                            ref={searchInputRef}
                            className="NewsBrowseSection__search-input"
                            type="text"
                            placeholder="Search News"
                            value={localSearchQuery}
                            onChange={onSearchChange}
                            onBlur={onSearchBlur}
                        />
                        {localSearchQuery && (
                            <Button className="NewsBrowseSection__search-clear" onClick={onSearchClear} options={['tertiary']}>
                                <IconClose />
                            </Button>
                        )}
                    </div>
                </div>

                {shouldShowResetButton(hasFilters, localSearchQuery) && (
                    <div className="Filters__column is-right">
                        <div className="Filters__reset">
                            <Button
                                className="Filters__reset-button"
                                onClick={() => {
                                    onSearchClear();
                                    onChange({
                                        ...filters,
                                        tagsIds: [],
                                        sortOrder: 'desc',
                                        orderBy: 'publishedDate',
                                        searchQuery: ''
                                    });
                                }}
                                options={['tertiary']}
                                unstyled
                            >
                                <div className="Filters__reset-icon">
                                    <IconReset />
                                </div>
                                Reset all
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FiltersBar;
